import type { LearningAssessmentStoreState } from "@/store/learning/assessment/state";
import {
    createExternalDirectorState,
    createProSkillAssureState,
    createTrackInfoState
} from "@/store/learning/state/factories";
import { LevelAssessmentModel } from "~~/package/mapper/levelAssessment/model/LevelAssessmentModel";
import {
    ProSkillAssureAssessmentModel
} from "~~/package/mapper/proSkillAssure/model/assessment/ProSkillAssureAssessmentModel";
import {
    ProSkillAssureAssessmentMyInfoModel
} from "~~/package/mapper/proSkillAssure/model/assessment/myInfo/ProSkillAssureAssessmentMyInfoModel";
import {
    ProSkillAssureAssessmentSetModel
} from "~~/package/mapper/proSkillAssure/model/assessment/ProSkillAssureAssessmentSetModel";
import { TaskModel } from "~~/package/mapper/learning/model/TaskModel";

export const assessmentActions = {
    resetAssessment(): void {
        (this as LearningAssessmentStoreState).assessmentList = [];
        (this as LearningAssessmentStoreState).assessmentAnswerList = [];
        (this as LearningAssessmentStoreState).isTimeOut = false;
    },
    resetProSkillAssure(): void {
        (this as LearningAssessmentStoreState).proSkillAssure = createProSkillAssureState();
        (this as LearningAssessmentStoreState).externalDirector = createExternalDirectorState();
    },
    resetContestData(): void {
        (this as LearningAssessmentStoreState).contestId = -1;
        (this as LearningAssessmentStoreState).contestTitle = "";
        (this as LearningAssessmentStoreState).taskTestTemplateId = -1;
        (this as LearningAssessmentStoreState).contestList = [];
    },
    resetLevelAssessment(): void {
        (this as LearningAssessmentStoreState).trackInfo = createTrackInfoState();
        (this as LearningAssessmentStoreState).levelAssessmentIdList = [];
        (this as LearningAssessmentStoreState).isTimeOut = false;
        (this as LearningAssessmentStoreState).taskSolveTime = 0;
        (this as LearningAssessmentStoreState).solvedQuestion = 0;
    },
    updateTrackLevelInfo(trackInfo: LevelAssessmentModel, levelId: string): void {
        (this as LearningAssessmentStoreState).trackInfo = {
            title: trackInfo.title,
            levelId,
            trackLevel: trackInfo.level,
            trackLevelTitle: trackInfo.trackLevelTitle
        };
    },
    updateProSkillAssessmentInfo(id: number, assessment: ProSkillAssureAssessmentModel, myInfo: ProSkillAssureAssessmentMyInfoModel, remainingTime?: number): void {
        // Preserve accumulated time-extension state across an assessment-info refresh.
        // The session-state poller seeds totalAddedSeconds/extensionHistory; re-initializing
        // the timer here must not wipe them (otherwise a refresh that races the poll makes the
        // extension badge/history disappear). TSK-10609.
        const prevTimer = (this as LearningAssessmentStoreState).proSkillAssure?.timer;
        (this as LearningAssessmentStoreState).proSkillAssure = {
            ...(this as LearningAssessmentStoreState).proSkillAssure,
            assessmentId: id,
            assessment,
            myInfo
        };
        if (remainingTime) {
            (this as LearningAssessmentStoreState).proSkillAssure.timer = {
                isStart: false,
                elapsedTime: 0,
                remainingTime,
                timerIntervalId: -1,
                extensionHistory: prevTimer?.extensionHistory ?? [],
                totalAddedSeconds: prevTimer?.totalAddedSeconds ?? 0,
                extensionFlashActive: false,
            };
        }
    },
    getNextAssessmentId(taskId: number): number {
        const assessmentList: TaskModel[] = (this as LearningAssessmentStoreState).assessmentList;
        const currentIndex = assessmentList.findIndex(assessment => assessment.id === taskId);
        if (currentIndex === -1) return 0;
        return currentIndex + 1 < assessmentList.length ? assessmentList[currentIndex + 1].id : 0;
    },
    getNextLevelAssessmentId(taskTemplateId: number): number {
        const levelAssessmentIdList: number[] = (this as LearningAssessmentStoreState).levelAssessmentIdList;
        const currentIndex = levelAssessmentIdList.findIndex(id => id === taskTemplateId);
        if (currentIndex === -1) return 0;
        return currentIndex + 1 < levelAssessmentIdList.length ? levelAssessmentIdList[currentIndex + 1] : 0;
    },
    getCurrentProSkillAssureSet(taskId: number): ProSkillAssureAssessmentSetModel {
        const setList = (this as LearningAssessmentStoreState).proSkillAssure.assessment.setList
            .filter(set => set.taskList.some(task => task.id === taskId));
        if (setList.length === 0) return new ProSkillAssureAssessmentSetModel();
        return setList[0];
    },
    getCurrentProSkillAssureSetIndex(taskId: number): number {
        return (this as LearningAssessmentStoreState).proSkillAssure.assessment.setList
            .findIndex(set => set.taskList.some(task => task.id === taskId));
    },
    applyExtensionEvent(event: { addedSeconds: number; newEndedAt: string; extensionCount: number; totalAddedSeconds: number; serverNow: string }): void {
        const state = this as LearningAssessmentStoreState;
        this.syncTimerDeadline(event.newEndedAt, event.serverNow);
        state.proSkillAssure.timer.extensionHistory.push({
            count: event.extensionCount,
            addedSeconds: event.addedSeconds,
            receivedAt: new Date().toISOString(),
        });
        state.proSkillAssure.timer.totalAddedSeconds = event.totalAddedSeconds;
        state.proSkillAssure.timer.extensionFlashActive = true;
    },
    // Timer-only re-sync: no history row or reveal, since the candidate was not granted an extension.
    syncTimerDeadline(newEndedAt: string, serverNow: string): void {
        const state = this as LearningAssessmentStoreState;
        const endedAtMs = new Date(newEndedAt).getTime();
        const serverNowMs = new Date(serverNow).getTime();
        state.proSkillAssure.timer.remainingTime = Math.max(0, Math.floor((endedAtMs - serverNowMs) / 1000));
        state.proSkillAssure.timer.elapsedTime = 0;
    }
};
